import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from 'vue';
import { useStore } from "@/hooks/useStore";
import { useStoreState } from "@/hooks/useStoreState";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import TrainingVideosRepository from "@/utils/API/TrainingVideoRepository";
import VBreadcrumbs from "@/components/vBreadcrumbs.vue";
import VModal from "@/components/vModal.vue";
import CollapsableCardWithHeader from "@/components/bootstrap/CollapsableCardWithHeader.vue";

interface Video {
  id: number;
  name: string;
  categoryId: number;
  tutorialLink: string;
  description: string;
  youtubeLink: string;
}

interface Category {
  id: number;
  name: string;
  videos: Video[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TrainingVideos',
  setup(__props) {

const categories = ref<Category[]>([]);
const defaultThumbnail = "/img/apple-profile.fd379455.jpg";
const store = useStore();
const storeState = useStoreState();
const viewMode = ref<'grid' | 'list'>('grid');

// Video Modal
const showVideoModal = ref(false);
const selectedVideoUrl = ref("");
const selectedVideoTitle = ref("");
const showInteractiveDemoModal = ref(false);
const selectedInteractiveDemoSrc = ref("");
const selectedInteractiveDemoTitle = ref("");
const isInteractiveDemoLoading = ref(false);

const translationOrFallback = (key: string, fallback: string) => {
  const translation = storeState.value.LocalizationModule.translations.get(key);
  return translation === "---" ? fallback : translation;
};

const isSupademo = (link: string) => {
  return !!extractSupademoEmbedSrc(link);
};

const getThumbnail = (video: Video) => {
  if (video.youtubeLink) {
    const match = video.youtubeLink.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/
    );

    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
  }

  return defaultThumbnail;
};


function extractSupademoEmbedSrc(input?: string) {
  if (!input) return "";

  const trimmed = input.trim();
  const iframeSrcMatch = trimmed.match(/src=["']([^"']+)["']/i);
  const candidate = iframeSrcMatch?.[1] || trimmed;

  if (!candidate.includes("supademo.com")) {
    return "";
  }

  if (candidate.includes("/embed/")) {
    return candidate;
  }

  if (candidate.includes("/demo/")) {
    const embedUrl = candidate.replace("/demo/", "/embed/");
    return embedUrl.includes("embed_v=")
      ? embedUrl
      : `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}embed_v=2&utm_source=embed`;
  }

  return candidate;
}

function openInteractiveDemoModal(video: Video) {
  const embedSrc = extractSupademoEmbedSrc(video.tutorialLink);
  if (!embedSrc) return;

  selectedInteractiveDemoSrc.value = embedSrc;
  selectedInteractiveDemoTitle.value = video.name;
  isInteractiveDemoLoading.value = true;
  showInteractiveDemoModal.value = true;
}

function closeInteractiveDemoModal() {
  showInteractiveDemoModal.value = false;
  selectedInteractiveDemoSrc.value = "";
  selectedInteractiveDemoTitle.value = "";
  isInteractiveDemoLoading.value = false;
}

function handleInteractiveDemoLoad() {
  isInteractiveDemoLoading.value = false;
}

function openVideoModal(video: Video) {
  if (!video.youtubeLink) return;

  selectedVideoUrl.value = video.youtubeLink;
  selectedVideoTitle.value = video.name;
  showVideoModal.value = true;
}

function closeVideoModal() {
  showVideoModal.value = false;
  selectedVideoUrl.value = "";
  selectedVideoTitle.value = "";
}

function getYoutubeEmbedUrl(url: string) {
  if (!url) return "";

  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&]+)/,
    /(?:youtu\.be\/)([^?&]+)/,
    /(?:youtube\.com\/shorts\/)([^?&]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://www.youtube.com/embed/${match[1]}?autoplay=1`;
    }
  }

  return "";
}

onMounted(async() => {
  const { data } = await TrainingVideosRepository.listCategories();
  const { data: videos } = await TrainingVideosRepository.listVideos();

  categories.value = data
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
    .map((category: any) => ({
      ...category,
      videos: videos
        .filter((v: any) => v.categoryId === category.id)
        .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
    }))
    .filter((category: any) => category.videos.length > 0);

  await store.value.dispatch(TRANSLATIONS_FETCH, {
    category: "pages",
    collections: ["common", "ingredients", "ingredient cost"],
  });
});


return { __sfc: true,categories, defaultThumbnail, store, storeState, viewMode, showVideoModal, selectedVideoUrl, selectedVideoTitle, showInteractiveDemoModal, selectedInteractiveDemoSrc, selectedInteractiveDemoTitle, isInteractiveDemoLoading, translationOrFallback, isSupademo, getThumbnail, extractSupademoEmbedSrc, openInteractiveDemoModal, closeInteractiveDemoModal, handleInteractiveDemoLoad, openVideoModal, closeVideoModal, getYoutubeEmbedUrl, VBreadcrumbs, VModal, CollapsableCardWithHeader }
}

})